import React from "react";
import { useTranslation } from "react-i18next";
import PageBreadcrumb from "@/components/common/PageBreadCrumb";
import PageMeta from "@/components/common/PageMeta";
import { RentingsTable } from "@/features/rentings/components/RentingsTable";
import { useRentings, RENTINGS_PER_PAGE } from '@/features/rentings/hooks/useRentings';
import { useSearchParams } from 'react-router-dom';
import Pagination from "@/components/common/Pagination";
import { RentingsParams } from '@/features/rentings/api/rentingsApi';
import { useCars } from '@/features/cars/hooks/useCars';
import { Loader } from "@/components/ui/loader/Loader";
import { TableColumnsFilter } from '@/components/ui/table/TableColumnsFilter';
import RentingFilters from '@/features/rentings/components/RentingFilters';
import { useHotkeys } from '@/shared/hooks/useHotkeys';

// Ключи столбцов должны соответствовать ключам в RentingsTable
const RENTINGS_COLUMNS = ['car', 'delivery', 'status', 'client', 'price', 'comments', 'reports', 'actions'];
const RENTINGS_TABLE_STORAGE_KEY = 'rentings-table-columns';

const RentingsPage: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const currentPage = Number(searchParams.get('page')) || 1;

  // Получаем текущие фильтры из URL
  const currentFilters: Partial<RentingsParams> = {
    status: searchParams.get('status') || undefined,
    car: searchParams.get('car') || undefined,
  };

  const { data, isLoading } = useRentings(currentPage, currentFilters);
  const { data: cars, isLoading: carsLoading } = useCars({ limit: 300 });

  const handleResetFilters = () => {
    const params = new URLSearchParams(searchParams);
    // Сохраняем текущую сортировку
    const currentOrder = params.get('order');
    // Очищаем все фильтры
    params.delete('status');
    params.delete('car');
    params.set('page', '1');
    // Восстанавливаем сортировку
    if (currentOrder) {
      params.set('order', currentOrder);
    }
    setSearchParams(params);
  };

  const handleSetConfirmedStatus = () => {
    const params = new URLSearchParams(searchParams);
    params.set('status', 'confirmed');
    params.set('page', '1'); // Сбрасываем страницу при изменении фильтра
    setSearchParams(params);
  };

  // Добавляем хоткеи
  useHotkeys([
    {
      key: 'c',
      callback: handleSetConfirmedStatus
    },
    {
      key: 'с',
      callback: handleSetConfirmedStatus
    },
    {
      key: 'r',
      callback: handleResetFilters
    },
    {
      key: 'к',
      callback: handleResetFilters
    }
  ]);

  const handleFilterChange = (newFilters: Partial<RentingsParams>) => {
    const params = new URLSearchParams(searchParams);

    // Обновляем параметры фильтров
    Object.entries(newFilters).forEach(([key, value]) => {
      if (value) {
        params.set(key, value.toString()); // преобразуем значение в строку
      } else {
        params.delete(key);
      }
    });

    // Сбрасываем страницу при изменении фильтров
    params.set('page', '1');

    setSearchParams(params);
  };

  const handleSort = (orderBy: string) => {
    const params = new URLSearchParams(searchParams);
    params.set('order', orderBy);
    setSearchParams(params);
  };

  const handlePageChange = (page: number) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', page.toString());
    setSearchParams(params);
  };

  // Определяем столбцы для фильтра, ключи должны соответствовать ключам в RentingsTable
  const tableColumns = [
    { key: 'id', header: t('rentings.columns.id'), field: 'id' },
    { key: 'car', header: t('rentings.columns.car'), field: 'car' },
    { key: 'delivery', header: t('rentings.columns.delivery'), field: 'delivery' },
    { key: 'status', header: t('rentings.columns.status'), field: 'status' },
    { key: 'client', header: t('rentings.columns.client'), field: 'client' },
    { key: 'price', header: t('rentings.columns.price'), field: 'total_price' },
    { key: 'comments', header: t('rentings.columns.comments'), field: 'comments' },
    { key: 'reports', header: t('rentings.columns.reports'), field: 'reports' },
    { key: 'actions', header: t('rentings.columns.actions'), field: 'actions' },
  ];

  return (
    <>
      <PageMeta
        title={t('rentings.title')}
        description={t('rentings.description')}
      />
      <PageBreadcrumb
        items={[
          { label: t('rentings.breadcrumb'), path: '/rentings' }
        ]}
      />

      <div className="space-y-6">
        <TableColumnsFilter
          columns={tableColumns}
          storageKey={RENTINGS_TABLE_STORAGE_KEY}
          defaultColumns={RENTINGS_COLUMNS}
        >
          <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
            <div className="flex flex-col md:flex-row gap-4 items-start md:items-center flex-1">
              <RentingFilters
                currentFilters={currentFilters}
                onFilterChange={handleFilterChange}
                cars={cars}
                isLoadingCars={carsLoading}
              />
            </div>
            {!isLoading && !carsLoading && (
              <div className="flex justify-start md:justify-end">
                <Pagination
                  totalItems={data?.total || 0}
                  itemsPerPage={RENTINGS_PER_PAGE}
                  currentPage={currentPage}
                  onPageChange={handlePageChange}
                />
              </div>
            )}
          </div>

          {isLoading || carsLoading ? (
            <div className="flex items-center justify-center min-h-[400px] bg-bg-secondary rounded-lg">
              <Loader />
            </div>
          ) : (
            <>
              <div className="bg-bg-secondary rounded-lg">
                <RentingsTable
                  rentings={data?.data || []}
                  cars={cars || []}
                  isLoading={false}
                  onSort={handleSort}
                />
              </div>
              <div className="flex justify-start md:justify-end mt-6">
                <Pagination
                  totalItems={data?.total || 0}
                  itemsPerPage={RENTINGS_PER_PAGE}
                  currentPage={currentPage}
                  onPageChange={handlePageChange}
                />
              </div>
            </>
          )}
        </TableColumnsFilter>
      </div>
    </>
  );
};

export default RentingsPage;
